<script setup lang="ts">
import { useAuthStore } from '~/store/auth'
import { useWebStore } from '~/store/web'
import { useIsMobile } from '~/composables/useIsMobile'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const webStore = useWebStore()

const isMobile = useIsMobile()
const drawerOpen = useState('wb-drawer-open', () => false)

const dropdownOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

const breadcrumbTitle = computed(() => {
  if (webStore.selectedWebsite) return webStore.selectedWebsite.title
  return (route.meta.title as string) || 'Přehled'
})

function selectWeb(web: typeof webStore.selectedWebsite) {
  webStore.setSelected(web)
  dropdownOpen.value = false
}

onClickOutside(dropdownRef, () => {
  dropdownOpen.value = false
})

// One entry per SITE: language versions are separate webs sharing a
// `language_group`, but for the owner they are one web (switched by the
// editor's language tabs). One account = one site today, so the switcher only
// appears for a member who really works on two different sites.
const siteWebs = computed(() => {
  const bySite = new Map<string, (typeof webStore.websites)[number]>()
  for (const w of webStore.websites) {
    const key = String((w as any).language_group || `web-${w.id}`)
    const prev = bySite.get(key)
    // Main version first: Czech, else whichever came first.
    if (!prev || ((w as any).language === 'cs' && (prev as any).language !== 'cs')) bySite.set(key, w)
  }
  return [...bySite.values()]
})
const showSwitcher = computed(() => !authStore.user?.is_admin_role && siteWebs.value.length > 1)
function isSelectedSite(web: (typeof webStore.websites)[number]) {
  const sel = webStore.selectedWebsite as any
  if (!sel) return false
  const g = (web as any).language_group
  return g ? sel.language_group === g : sel.id === web.id
}

// "Zobrazit web" chip — populated by the web editor (pages/web-edit) while editing a web.
const viewWeb = useState<{
  href: string
  domain: string | null
  hidden?: string | null
  deactivated?: boolean
} | null>('wb-view-web', () => null)

const feedbackOpen = ref(false)
</script>

<template>
  <header
    :style="{ padding: isMobile ? '14px 16px' : '18px 36px' }"
    style="
      display: flex;
      align-items: center;
      gap: 12px;
      border-bottom: 1px solid var(--hair);
      background: var(--cream);
      position: sticky;
      top: 0;
      z-index: 10;
      backdrop-filter: blur(8px);
    "
  >
    <!-- Hamburger on mobile -->
    <button
      v-if="isMobile"
      @click="drawerOpen = true"
      style="
        width: 36px;
        height: 36px;
        border: 1px solid var(--hair);
        background: var(--paper);
        border-radius: var(--r-md);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
        color: var(--ink-2);
      "
      aria-label="Otevřít menu"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    </button>

    <div
      style="
        display: flex;
        align-items: center;
        gap: 10px;
        color: var(--ink-3);
        font-size: 13px;
        font-family: var(--sans);
        min-width: 0;
      "
    >
      <span v-if="!isMobile" style="display: inline-flex; align-items: center; gap: 2px">
        <BrandMark :size="22" />
        <span style="font-weight: 700; letter-spacing: -0.015em; color: var(--ink); font-size: 14px">qCMS</span>
      </span>
      <svg
        v-if="!isMobile"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M9 6l6 6-6 6" />
      </svg>
      <span
        style="
          color: var(--ink);
          font-family: var(--serif);
          font-style: italic;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        "
        :style="{ fontSize: isMobile ? '16px' : '15px' }"
      >
        {{ breadcrumbTitle }}
      </span>
    </div>

    <div style="flex: 1" />

    <div
      v-if="showSwitcher"
      ref="dropdownRef"
      style="position: relative; min-width: 0; flex-shrink: 1"
      :style="{ maxWidth: isMobile ? '42vw' : '280px' }"
    >
      <!-- One line, always: a long „Název webu (CS)" used to wrap into a 3-line
           blob on phones and push the page title away. Full name in the tooltip.
           The cap sits on the WRAPPER and the pill is max-width:100%: capped on
           the button only, the shrunk wrapper let the pill overflow onto the
           „Chyba / nápad" icon next to it (F055). -->
      <button
        class="wb-btn"
        style="
          padding: 7px 14px;
          font-size: 12px;
          border-radius: 999px;
          white-space: nowrap;
          max-width: 100%;
          min-width: 0;
          overflow: hidden;
        "
        :title="webStore.selectedWebsite?.title || ''"
        @click="dropdownOpen = !dropdownOpen"
      >
        <span style="display: block; min-width: 0; overflow: hidden; text-overflow: ellipsis">{{
          webStore.selectedWebsite?.title || 'Vyberte web'
        }}</span>
      </button>

      <div
        v-if="dropdownOpen"
        style="
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: var(--paper);
          border: 1px solid var(--hair);
          border-radius: var(--r-lg);
          box-shadow: 0 8px 32px rgba(31, 27, 22, 0.1);
          min-width: 220px;
          z-index: 100;
          overflow: hidden;
          padding: 6px;
        "
      >
        <button
          v-for="web in siteWebs"
          :key="web.id"
          @click="selectWeb(web)"
          style="
            width: 100%;
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 12px;
            border: none;
            border-radius: var(--r-md);
            cursor: pointer;
            font-family: var(--sans);
            font-size: 13px;
            background: transparent;
            text-align: left;
          "
          :style="{
            background: isSelectedSite(web) ? 'var(--cream-2)' : 'transparent',
          }"
        >
          {{ web.title }}
        </button>
      </div>
    </div>

    <!-- A language version never sent to the web: visitors of /de/ still get
         the main language, so never „Web je online …/de" (set by web-edit). -->
    <a
      v-if="!isMobile && viewWeb && viewWeb.hidden"
      :href="viewWeb.href"
      target="_blank"
      rel="noopener"
      class="sb-viewweb draft"
      data-qa="lang-hidden-chip"
      title="Návštěvníci tento jazyk zatím nevidí — zobrazí se po „Poslat na web“. Otevře náhled."
    >
      <span class="sb-viewweb__dot" />
      <span class="sb-viewweb__meta">
        <span class="sb-viewweb__kicker">Jazyk {{ viewWeb.hidden }} zatím není vidět</span>
        <span class="sb-viewweb__val">zobrazí se po „Poslat na web“</span>
      </span>
      <span class="sb-viewweb__sep" />
      <span class="sb-viewweb__cta">Náhled</span>
    </a>

    <!-- F086: a deactivated (unpaid) web is NOT online for visitors (overlay
         + 503) — never claim it is; lead to the payment instead. -->
    <NuxtLink
      v-else-if="!isMobile && viewWeb && viewWeb.deactivated"
      to="/invoices/"
      class="sb-viewweb draft"
      data-qa="web-deactivated-chip"
      title="Web je vypnutý kvůli nezaplacené faktuře — návštěvníci ho nevidí"
    >
      <span class="sb-viewweb__dot" />
      <span class="sb-viewweb__meta">
        <span class="sb-viewweb__kicker">Web je vypnutý</span>
        <span class="sb-viewweb__val">{{ viewWeb.domain || 'návštěvníci ho nevidí' }}</span>
      </span>
      <span class="sb-viewweb__sep" />
      <span class="sb-viewweb__cta">Platby</span>
    </NuxtLink>

    <!-- "Zobrazit web" — live web link, shown in the editor (desktop) -->
    <a
      v-else-if="!isMobile && viewWeb && viewWeb.domain"
      :href="viewWeb.href"
      target="_blank"
      rel="noopener"
      class="sb-viewweb online"
      title="Otevřít web v novém okně"
    >
      <span class="sb-viewweb__dot" />
      <span class="sb-viewweb__meta">
        <span class="sb-viewweb__kicker">Web je online</span>
        <span class="sb-viewweb__val">{{ viewWeb.domain }}</span>
      </span>
      <span class="sb-viewweb__sep" />
      <span class="sb-viewweb__cta">
        Zobrazit
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M15 3h6v6" />
          <path d="M10 14 21 3" />
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        </svg>
      </span>
    </a>

    <!-- No domain yet → the chip says so plainly and leads to the fix (/domains) -->
    <NuxtLink
      v-else-if="!isMobile && viewWeb"
      to="/domains/"
      class="sb-viewweb draft"
      title="Web zatím nemá veřejnou adresu — nastavte ji"
    >
      <span class="sb-viewweb__dot" />
      <span class="sb-viewweb__meta">
        <span class="sb-viewweb__kicker">Web zatím nemá adresu</span>
        <span class="sb-viewweb__val">Není veřejný — jen náhled</span>
      </span>
      <span class="sb-viewweb__sep" />
      <span class="sb-viewweb__cta">
        Nastavit adresu
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M5 12h14" />
          <path d="m12 5 7 7-7 7" />
        </svg>
      </span>
    </NuxtLink>

    <button
      type="button"
      class="sb-feedback"
      :class="{ 'sb-feedback--icon': isMobile }"
      title="Nahlásit chybu nebo navrhnout nápad"
      aria-label="Nahlásit chybu nebo navrhnout nápad"
      @click="feedbackOpen = true"
    >
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <path d="M12 7v4" />
        <path d="M12 14h.01" />
      </svg>
      <span v-if="!isMobile">Chyba / nápad</span>
    </button>
    <FeedbackModal v-if="feedbackOpen" @close="feedbackOpen = false" />

    <div v-if="!isMobile" style="font-size: 12px; color: var(--ink-3); font-family: var(--mono)">
      {{ authStore.user?.email }}
    </div>
  </header>
</template>

<style scoped>
.sb-feedback {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 13px;
  border-radius: 999px;
  border: 1px solid var(--hair);
  background: var(--paper);
  color: var(--ink-2);
  font-family: var(--sans);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    border-color 0.15s,
    color 0.15s;
}
.sb-feedback:hover {
  border-color: var(--accent);
  color: var(--accent);
}
.sb-feedback--icon {
  width: 36px;
  height: 36px;
  padding: 0;
  justify-content: center;
  border-radius: var(--r-md);
}
.sb-viewweb {
  display: inline-flex;
  align-items: center;
  gap: 11px;
  height: 38px;
  padding: 0 12px 0 13px;
  border-radius: 11px;
  background: var(--paper);
  border: 1px solid var(--hair);
  text-decoration: none;
  box-shadow: 0 1px 2px rgba(20, 25, 35, 0.06);
  transition:
    box-shadow 0.18s,
    transform 0.18s,
    border-color 0.18s;
}
.sb-viewweb:hover {
  box-shadow: 0 6px 16px -6px rgba(31, 27, 22, 0.28);
  transform: translateY(-1px);
}
.sb-viewweb.draft {
  background: #fdf6ec;
  border-color: #e4cfa3;
  box-shadow: none;
}
.sb-viewweb.draft:hover {
  background: #fbf0dd;
  border-color: #d4b87f;
  transform: none;
  box-shadow: 0 4px 12px -6px rgba(150, 108, 32, 0.35);
}
.sb-viewweb__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #b3b9cc;
  flex: none;
}
.sb-viewweb.draft .sb-viewweb__dot {
  background: #d19a3d;
}
.sb-viewweb.online .sb-viewweb__dot {
  background: #4a8c5a;
  animation: sbPulse 2.2s infinite;
}
@keyframes sbPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(74, 140, 90, 0.45);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(74, 140, 90, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(74, 140, 90, 0);
  }
}
.sb-viewweb__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
}
.sb-viewweb__kicker {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #4a8c5a;
}
.sb-viewweb.draft .sb-viewweb__kicker {
  color: #a4762a;
}
.sb-viewweb__val {
  font-size: 13px;
  font-weight: 700;
  color: var(--ink);
}
.sb-viewweb.draft .sb-viewweb__val {
  color: var(--ink-3);
}
.sb-viewweb__sep {
  width: 1px;
  height: 20px;
  background: var(--hair);
}
.sb-viewweb.draft .sb-viewweb__sep {
  background: #e4cfa3;
}
.sb-viewweb__cta {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--accent);
}
.sb-viewweb.draft .sb-viewweb__cta {
  color: #a4762a;
}
</style>
